import { findByIdWithRelationsQuery } from './engine-model.graphql';
import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import { DigitaiChartVariable, DigitaiCreateEngineModel, DigitaiEngineModel, DigitaiEngineModelChartVariable, DigitaiEngineModelPotentialIssue, DigitaiPotentialIssue, DigitaiUpdateEngineModelById, DigitaiUpdateEngineModels } from '@apps/digitai';
import { ChartVariableService } from '@apps/digitai/chart-variable';
import { createMutation, deleteByIdMutation, deleteMutation, fields, findByIdQuery, findQuery, getQuery, insertMutation, paginationQuery, updateByIdMutation, updateMutation } from '@apps/digitai/engine-model';
import { EngineModelChartVariableService } from '@apps/digitai/engine-model-chart-variable';
import { EngineModelPotentialIssueService } from '@apps/digitai/engine-model-potential-issue';
import { PotentialIssueService } from '@apps/digitai/potential-issue';
import { GraphQLHeaders, GraphQLService, GridData, parseGqlFields, QueryStatement } from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EngineModelService
{
    paginationSubject$: BehaviorSubject<GridData<DigitaiEngineModel> | null> = new BehaviorSubject(null);
    engineModelSubject$: BehaviorSubject<DigitaiEngineModel | null> = new BehaviorSubject(null);
    engineModelsSubject$: BehaviorSubject<DigitaiEngineModel[] | null> = new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: { [key: string]: BehaviorSubject<GridData<DigitaiEngineModel> | null>; } = {};
    engineModelScoped: { [key: string]: BehaviorSubject<DigitaiEngineModel | null>; } = {};
    engineModelsScoped: { [key: string]: BehaviorSubject<DigitaiEngineModel[] | null>; } = {};

    constructor(
        private readonly chartVariableService: ChartVariableService,
        private readonly engineModelChartVariableService: EngineModelChartVariableService,
        private readonly engineModelPotentialIssueService: EngineModelPotentialIssueService,
        private readonly graphqlService: GraphQLService,
        private readonly potentialIssueService: PotentialIssueService,
    ) {}

    /**
    * Getters
    */
    get pagination$(): Observable<GridData<DigitaiEngineModel>>
    {
        return this.paginationSubject$.asObservable();
    }

    get engineModel$(): Observable<DigitaiEngineModel>
    {
        return this.engineModelSubject$.asObservable();
    }

    get engineModels$(): Observable<DigitaiEngineModel[]>
    {
        return this.engineModelsSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(scope: string, pagination: GridData<DigitaiEngineModel>): void
    {
        if (this.paginationScoped[scope])
        {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiEngineModel>>
    {
        if (this.paginationScoped[scope]) return this.paginationScoped[scope].asObservable();

        this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeEngineModel(scope: string, object: DigitaiEngineModel): void
    {
        if (this.engineModelScoped[scope])
        {
            this.engineModelScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.engineModelScoped[scope] = new BehaviorSubject(object);
    }

    getScopeEngineModel(scope: string): Observable<DigitaiEngineModel>
    {
        if (this.engineModelScoped[scope]) return this.engineModelScoped[scope].asObservable();

        this.engineModelScoped[scope] = new BehaviorSubject(null);
        return this.engineModelScoped[scope].asObservable();
    }

    setScopeEngineModels(scope: string, objects: DigitaiEngineModel[]): void
    {
        if (this.engineModelsScoped[scope])
        {
            this.engineModelsScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.engineModelsScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeEngineModels(scope: string): Observable<DigitaiEngineModel[]>
    {
        if (this.engineModelsScoped[scope]) return this.engineModelsScoped[scope].asObservable();

        this.engineModelsScoped[scope] = new BehaviorSubject(null);
        return this.engineModelsScoped[scope].asObservable();
    }

    pagination(
        {
            graphqlStatement = paginationQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<GridData<DigitaiEngineModel>>
    {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiEngineModel>; }>({
                query    : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data.pagination),
                tap(pagination => scope ? this.setScopePagination(scope, pagination) : this.paginationSubject$.next(pagination)),
            );
    }

    findById(
        {
            graphqlStatement = findByIdQuery,
            id = null,
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModel;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModel;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModel(scope, data.object) : this.engineModelSubject$.next(data.object)),
            );
    }

    findByIdWithRelations(
        {
            graphqlStatement = findByIdWithRelationsQuery,
            id = '',
            constraint = {},
            queryPaginateEngineModelsPotentialIssues = {},
            constraintPaginateEngineModelsPotentialIssues = {},
            queryPaginatePotentialIssues = {},
            constraintPaginatePotentialIssues = {},
            queryGetEngineModelsPotentialIssues = {},
            constraintGetEngineModelsPotentialIssues = {},
            queryPaginateEngineModelsChartVariables = {},
            constraintPaginateEngineModelsChartVariables = {},
            queryPaginateChartVariables = {},
            constraintPaginateChartVariables = {},
            queryGetEngineModelsChartVariables = {},
            constraintGetEngineModelsChartVariables = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            queryPaginateEngineModelsPotentialIssues?: QueryStatement;
            constraintPaginateEngineModelsPotentialIssues?: QueryStatement;
            queryPaginatePotentialIssues?: QueryStatement;
            constraintPaginatePotentialIssues?: QueryStatement;
            queryGetEngineModelsPotentialIssues?: QueryStatement;
            constraintGetEngineModelsPotentialIssues?: QueryStatement;
            queryPaginateEngineModelsChartVariables?: QueryStatement;
            constraintPaginateEngineModelsChartVariables?: QueryStatement;
            queryPaginateChartVariables?: QueryStatement;
            constraintPaginateChartVariables?: QueryStatement;
            queryGetEngineModelsChartVariables?: QueryStatement;
            constraintGetEngineModelsChartVariables?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModel;
        digitaiPaginateEngineModelsPotentialIssues: GridData<DigitaiEngineModelPotentialIssue>;
        digitaiPaginatePotentialIssues: GridData<DigitaiPotentialIssue>;
        digitaiGetEngineModelsPotentialIssues: DigitaiEngineModelPotentialIssue[];
        digitaiPaginateEngineModelsChartVariables: GridData<DigitaiEngineModelChartVariable>;
        digitaiPaginateChartVariables: GridData<DigitaiChartVariable>;
        digitaiGetEngineModelsChartVariables: DigitaiEngineModelChartVariable[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModel;
                digitaiPaginateEngineModelsPotentialIssues: GridData<DigitaiEngineModelPotentialIssue>;
                digitaiPaginatePotentialIssues: GridData<DigitaiPotentialIssue>;
                digitaiGetEngineModelsPotentialIssues: DigitaiEngineModelPotentialIssue[];
                digitaiPaginateEngineModelsChartVariables: GridData<DigitaiEngineModelChartVariable>;
                digitaiPaginateChartVariables: GridData<DigitaiChartVariable>;
                digitaiGetEngineModelsChartVariables: DigitaiEngineModelChartVariable[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryPaginateEngineModelsPotentialIssues,
                    constraintPaginateEngineModelsPotentialIssues: {
                        ...constraintPaginateEngineModelsPotentialIssues,
                        where: {
                            engineModelId: id,
                        },
                        include: [
                            {
                                association: 'potentialIssue',
                            },
                        ],
                    },
                    queryPaginatePotentialIssues,
                    constraintPaginatePotentialIssues,
                    queryGetEngineModelsPotentialIssues,
                    constraintGetEngineModelsPotentialIssues,
                    queryPaginateEngineModelsChartVariables,
                    constraintPaginateEngineModelsChartVariables: {
                        ...constraintPaginateEngineModelsChartVariables,
                        where: {
                            engineModelId: id,
                        },
                        include: [
                            {
                                association: 'chartVariable',
                            },
                        ],
                    },
                    queryPaginateChartVariables,
                    constraintPaginateChartVariables,
                    queryGetEngineModelsChartVariables,
                    constraintGetEngineModelsChartVariables,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data =>
                {
                    if (scope)
                    {
                        this.setScopeEngineModel(scope, data.object);
                    }
                    else
                    {
                        this.engineModelSubject$.next(data.object);
                    }
                    this.engineModelPotentialIssueService.paginationSubject$.next(data.digitaiPaginateEngineModelsPotentialIssues);
                    this.potentialIssueService.paginationSubject$.next(data.digitaiPaginatePotentialIssues);
                    this.engineModelPotentialIssueService.engineModelsPotentialIssuesSubject$.next(data.digitaiGetEngineModelsPotentialIssues);
                    this.engineModelChartVariableService.paginationSubject$.next(data.digitaiPaginateEngineModelsChartVariables);
                    this.chartVariableService.paginationSubject$.next(data.digitaiPaginateChartVariables);
                    this.engineModelChartVariableService.engineModelsChartVariablesSubject$.next(data.digitaiGetEngineModelsChartVariables);
                }),
            );
    }

    find(
        {
            graphqlStatement = findQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        object: DigitaiEngineModel;
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiEngineModel;
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModel(scope, data.object) : this.engineModelSubject$.next(data.object)),
            );
    }

    get(
        {
            graphqlStatement = getQuery,
            query = {},
            constraint = {},
            headers = {},
            scope,
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
            scope?: string;
        } = {},
    ): Observable<{
        objects: DigitaiEngineModel[];
    }>
    {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiEngineModel[];
            }>({
                query    : parseGqlFields(graphqlStatement, fields, query, constraint),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges
            .pipe(
                first(),
                map(result => result.data),
                tap(data => scope ? this.setScopeEngineModels(scope, data.objects) : this.engineModelsSubject$.next(data.objects)),
            );
    }

    create<T>(
        {
            graphqlStatement = createMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiCreateEngineModel;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    insert<T>(
        {
            graphqlStatement = insertMutation,
            objects = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            objects?: DigitaiCreateEngineModel[];
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: objects,
                },
                context: {
                    headers,
                },
            });
    }

    updateById<T>(
        {
            graphqlStatement = updateByIdMutation,
            object = null,
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModelById;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                },
                context: {
                    headers,
                },
            });
    }

    update<T>(
        {
            graphqlStatement = updateMutation,
            object = null,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            object?: DigitaiUpdateEngineModels;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    payload: object,
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    deleteById<T>(
        {
            graphqlStatement = deleteByIdMutation,
            id = null,
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            id?: string;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }

    delete<T>(
        {
            graphqlStatement = deleteMutation,
            query = {},
            constraint = {},
            headers = {},
        }: {
            graphqlStatement?: DocumentNode;
            query?: QueryStatement;
            constraint?: QueryStatement;
            headers?: GraphQLHeaders;
        } = {},
    ): Observable<FetchResult<T>>
    {
        return this.graphqlService
            .client()
            .mutate({
                mutation : graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            });
    }
}
